// src/views/Settings/org/OrgBillingSection.jsx
//
// La facturación consolidada de la organización: el pool enterprise, su
// suscripción, el consumo por cuenta y las cuotas.
//
// Vivía como pestaña hermana del saldo personal dentro de una pantalla titulada
// "Manage and update your personal information". Dos alcances distintos
// apilados en pestañas, con el mismo aspecto: un administrador podía estar
// mirando el saldo de la empresa creyendo que miraba el suyo. Eso no se arregla
// con una etiqueta, se arregla poniendo cada uno en su nivel.
//
// El dinero existe en los dos: `accounts` guarda el tier y el saldo de cada
// cuenta, `organization_subscriptions` el pool de la organización. No es
// duplicación — es facturación consolidada, como en AWS Organizations.
import React, { useState } from 'react';
import useOrgBalance from '../../../hooks/billing/useOrgBalance';
import useOrgSubscription from '../../../hooks/billing/useOrgSubscription';
import useOrgUsageByAccount from '../../../hooks/billing/useOrgUsageByAccount';
import useOrgQuotas from '../../../hooks/billing/useOrgQuotas';
import OrgBalanceCard from '../../Profile/components/OrgBalanceCard';
import OrgSubscriptionCard from '../../Profile/components/OrgSubscriptionCard';
import NotEnterpriseEmptyState from '../../Profile/components/NotEnterpriseEmptyState';
import UsageByAccountTable from '../../Profile/components/UsageByAccountTable';
import QuotasTable from '../../Profile/components/QuotasTable';
import AccountTransactionsDrawer from '../../Profile/components/AccountTransactionsDrawer';
import '../../Profile/WalletView.css';

// Los hooks devuelven este código cuando la organización no tiene pool. No es un
// fallo que mostrar: es el estado normal de una organización no enterprise.
const NOT_ENTERPRISE = 'NOT_AN_ENTERPRISE_ORG';
const realError = (error) =>
  error && error.code !== NOT_ENTERPRISE ? error : null;

export default function OrgBillingSection({ organizationId, organizationName, onViewPlans }) {
  const {
    data: balance,
    loading: balanceLoading,
    error: balanceError,
    refetch: refetchBalance,
  } = useOrgBalance(organizationId);

  const {
    data: subscription,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = useOrgSubscription(organizationId);

  const [usageRange, setUsageRange] = useState({ preset: 'period' });
  const {
    data: usageByAccount,
    loading: usageByAccountLoading,
    error: usageByAccountError,
  } = useOrgUsageByAccount(organizationId, {
    since: usageRange.since,
    until: usageRange.until,
  });

  const {
    data: quotas,
    loading: quotasLoading,
    error: quotasError,
  } = useOrgQuotas(organizationId);

  // Para bajar al libro mayor de una cuenta miembro concreta.
  const [drawerRow, setDrawerRow] = useState(null);

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetchBalance();
    } finally {
      setRefreshing(false);
    }
  };

  const isNotEnterprise =
    balanceError?.code === NOT_ENTERPRISE ||
    subscriptionError?.code === NOT_ENTERPRISE;

  if (isNotEnterprise) {
    return (
      <NotEnterpriseEmptyState
        organizationName={organizationName}
        onContactSales={onViewPlans}
      />
    );
  }

  return (
    <div className="wallet-container">
      <div className="wallet-grid">
        <div className="">
          <OrgBalanceCard
            totalBalance={balance?.totalBalance}
            periodBalance={balance?.periodBalance}
            extraBalance={balance?.extraBalance}
            customMonthlyCoinAllowance={balance?.customMonthlyCoinAllowance}
            lastResetAt={balance?.billingCycle?.lastResetAt}
            organizationName={organizationName}
            loading={balanceLoading && !balance}
            refreshing={refreshing}
            error={realError(balanceError)}
            onRefresh={handleRefresh}
          />
        </div>
        <div className="">
          <OrgSubscriptionCard
            subscription={subscription}
            loading={subscriptionLoading && !subscription}
            error={realError(subscriptionError)}
          />
        </div>
      </div>

      <div className="wallet-section">
        <UsageByAccountTable
          data={usageByAccount}
          range={usageRange}
          onRangeChange={setUsageRange}
          onRowClick={setDrawerRow}
          loading={usageByAccountLoading && !usageByAccount}
          error={realError(usageByAccountError)}
        />
      </div>

      <div className="wallet-section">
        <QuotasTable
          data={quotas}
          loading={quotasLoading && !quotas}
          error={realError(quotasError)}
        />
      </div>

      <AccountTransactionsDrawer
        open={Boolean(drawerRow)}
        accountId={drawerRow?.accountId ?? null}
        accountName={drawerRow?.accountName ?? null}
        onClose={() => setDrawerRow(null)}
      />
    </div>
  );
}
